import { PadManager } from "./PadManager"
import { VPServerConnection } from "./VPServerConnection"

export class PadSessionManager {
  private static instance: PadSessionManager | undefined
  private readonly serverConnection: VPServerConnection
  private readonly padManager: PadManager
  private connectionsClosed = true

  /**
   * Initializes the virtual pad session manager
   */
  private constructor () {
    this.serverConnection = VPServerConnection.getInstance()
    this.padManager = PadManager.getInstance()

    this.serverConnection.setOnUnexpectedClose(this.handleUnexpectedServerClose)
    // console.log('PAD SESSION MANAGER :: START SESSION :: Starting session')
    
    void this.startSession().catch(error => {
      console.error('PAD SESSION MANAGER :: START SESSION :: Could not start session', {
        cause: error
      })
    })
  }

  /**
   * Returns the PadSessionManager instance, creating it if necessary
   * 
   * @returns The PadSessionManager instance
   */
  public static getInstance (): PadSessionManager {
    if (PadSessionManager.instance === undefined) {
      PadSessionManager.instance = new PadSessionManager()
    }
    return PadSessionManager.instance
  }

  /**
   * Starts a virtual pad session and waits until it has been created
   */
  public async startSession(): Promise<void> {
    if (!this.connectionsClosed) return

    this.connectionsClosed = false

    try {
      await this.serverConnection.connect()
    } catch (error) {
      this.connectionsClosed = true
      throw error
    }
  }  

  /**
   * Closes the current virtual pad session
   * 
   * All virtual pads are closed before terminating the signaling 
   * server connection. Physical gamepads are preserved
   */
  public closeSession (): void {
    if (this.connectionsClosed) return

    this.connectionsClosed = true

    this.padManager.clearVPads()
    this.serverConnection.close()
  }

  /**
   * Handles the unexpected loss of the signaling server connection.
   *
   * Since the signaling session no longer exists, all virtual pads
   * are closed and removed. Physical gamepads are preserved.
   */
  private readonly handleUnexpectedServerClose = (): void => {
    this.connectionsClosed = true
    this.padManager.clearVPads()
  }

  /**
   * Get the current server connection
   * @returns Current server connection
   */
  public getServerConnection (): VPServerConnection {
    return this.serverConnection
  }

  /**
   * Get the current sessionID
   * @returns Current sessionID
   */
  public getSessionId (): string | null {
    return this.serverConnection.getSessionProtocol().getSessionId()
  }

  /**
   * Get the current padManager
   * @returns Current padManager
   */
  public getPadManager (): PadManager {
    return this.padManager
  }
}